import React, { FC, useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'
import { useScript } from 'hoofd'

import { HOST_NAME } from '@data/config/url'

import { Metadata } from '../Metadata'
import { buildBreadcrumbList, HOME_BREADCRUMB } from '../buildBreadcrumbList'

import { UserMetadata_user$key } from './__generated__/UserMetadata_user.graphql'

export const UserMetadata: FC<{
  user: UserMetadata_user$key
  title?: string
  description?: string
}> = ({ user, title, description }) => {
  const data = useFragment(
    graphql`
      fragment UserMetadata_user on User {
        slugId
        firstName
        lastName
        bio
        primaryPhoto {
          seoUrl: transformUrl(
            transform: { w: 1200, h: 630, dpr: 1, fit: "cover" }
          )
        }
      }
    `,
    user
  )

  const name = `${data.firstName} ${data.lastName}`
  const canonicalUrl = `${HOST_NAME}/${data.slugId}`

  const structuredData = useMemo(() => {
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Person',
      name,
      url: canonicalUrl,
      ...(description || data.bio
        ? { description: description || data.bio }
        : {}),
      ...(data.primaryPhoto?.seoUrl ? { image: data.primaryPhoto.seoUrl } : {}),
    })
  }, [data, name, description, canonicalUrl])

  useScript({
    type: 'application/ld+json',
    text: structuredData,
  })

  const breadcrumbData = useMemo(
    () => buildBreadcrumbList([HOME_BREADCRUMB, { name, url: canonicalUrl }]),
    [name, canonicalUrl]
  )

  useScript({ type: 'application/ld+json', text: breadcrumbData })

  return (
    <Metadata
      canonicalUrl={canonicalUrl}
      title={title || name}
      description={description || data.bio}
      photoUrl={data.primaryPhoto?.seoUrl}
      imageTransformParams='NONE'
    />
  )
}
